import { Link } from "expo-router";
import { useEffect, useMemo, useRef } from "react";
import {
  ActivityIndicator,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";

import { fontFamily, layout, radius, space, textStyle } from "@passfirst/ui";

import { Button } from "../src/components/button";
import { Screen } from "../src/components/screen";
import { ChoiceList } from "../src/practice/components/choice-list";
import { ExplanationCard } from "../src/practice/components/explanation-card";
import { PinnedFooter } from "../src/practice/components/pinned-footer";
import { ProgressHeader } from "../src/practice/components/progress-header";
import { ReportSection } from "../src/practice/components/report-section";
import { TopicStem } from "../src/practice/components/topic-stem";
import { TutorPanel } from "../src/practice/components/tutor-panel";
import { usePracticeSession } from "../src/practice/use-practice-session";
import { useReportFlow } from "../src/practice/use-report-flow";
import { useTutorChat } from "../src/practice/use-tutor-chat";
import { type Colors, useColors } from "../src/theme";
import { useResponsive } from "../src/use-responsive";

// Practice screen orchestrator. Hooks own state; components own
// presentation. This file's job is composition + per-question reset
// wiring (when the user advances, the tutor + report state for the
// just-finished question gets cleared).
export default function Practice(): JSX.Element {
  const colors = useColors();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const insets = useSafeAreaInsets();
  const { isWide } = useResponsive();

  const session = usePracticeSession();
  const tutor = useTutorChat({
    current: session.current,
    sessionIdRef: session.sessionIdRef,
  });
  const report = useReportFlow();

  // Reset scroll to the top when the user advances — otherwise the next
  // question opens scrolled to wherever the previous one was left. One
  // ref per pane (focused column / split right pane); only one is
  // mounted at a time, so we null-guard each.
  const focusedScrollRef = useRef<ScrollView>(null);
  const rightPaneScrollRef = useRef<ScrollView>(null);
  useEffect(() => {
    focusedScrollRef.current?.scrollTo({ y: 0, animated: false });
    rightPaneScrollRef.current?.scrollTo({ y: 0, animated: false });
  }, [session.index]);

  // Follow the tutor conversation: when a new bubble lands (message count
  // changes) or the "Thinking…" indicator toggles, scroll the active pane to
  // the bottom so the latest reply and the way-forward control stay in view.
  // Only one pane is mounted at a time, so each ref is null-guarded.
  useEffect(() => {
    if (tutor.messages.length === 0) return;
    focusedScrollRef.current?.scrollToEnd({ animated: true });
    rightPaneScrollRef.current?.scrollToEnd({ animated: true });
  }, [tutor.messages.length, tutor.sending]);

  // Live free-tier remaining — decrements as questions are answered this
  // batch, instead of showing the static start-of-batch count.
  const liveRemaining = session.entitlement?.freeTier
    ? Math.max(
        0,
        session.entitlement.freeTier.remaining - session.score.total,
      )
    : undefined;

  function handleSubmit(): void {
    void (async () => {
      const before = session.selected;
      // Use the result submit() RETURNS, not session.result — the latter is
      // the stale state value inside this closure (the setResult update isn't
      // visible until the next render), so reading it here would skip the
      // tutor kickoff on every wrong answer.
      const result = await session.submit();
      if (
        // Tutor is authed-only (tutor.explain). Anonymous users get the
        // signup nudge below instead of a Socratic kickoff.
        !session.isAnonymous &&
        session.current &&
        before &&
        result &&
        !result.correct
      ) {
        tutor.seedKickoff(before);
      }
    })();
  }

  function handleAdvance(): void {
    session.next(() => {
      tutor.reset();
      report.reset();
    });
  }

  if (session.error && !session.questions) {
    // Never surface the raw error string (tRPC codes like UNAUTHORIZED,
    // "failed to load") to users — show a friendly recovery screen. The
    // detail is still captured: failed fetches emit observa.error via the
    // tRPC wrapper.
    return (
      <View style={styles.container}>
        <Text style={styles.errorTitle}>We couldn&apos;t start practice</Text>
        <Text style={styles.error}>
          Something went wrong loading your questions. Check your connection and
          try again.
        </Text>
        <Link href="/" asChild>
          <Button label="Back to home" style={styles.button} />
        </Link>
      </View>
    );
  }

  if (!session.questions || !session.current) {
    return (
      <View style={styles.container}>
        <ActivityIndicator />
      </View>
    );
  }

  // Brief: "Submit button: orange CTA, full-width, fixed at bottom on
  // mobile." Hidden during the Socratic flow because the tutor panel
  // owns the primary action — but anonymous users have no tutor, so the
  // footer must stay to give them a way forward after a wrong answer.
  const showPinnedFooter =
    !session.result || session.result.correct || session.isAnonymous;
  const isLast = session.index + 1 >= session.questions.length;

  const progress = (
    <ProgressHeader
      current={session.index + 1}
      total={session.questions.length}
      entitlement={session.entitlement}
      liveRemaining={liveRemaining}
    />
  );
  const stem = (
    <TopicStem
      topicLabel={session.current.topicLabel}
      stem={session.current.stem}
      large={isWide}
    />
  );
  // The "act on this" stack: choices → explanation → tutor → nudge.
  // Shared between the focused column and the split right pane.
  const answerStack = (
    <>
      <ChoiceList
        choices={session.current.choices}
        selected={session.selected}
        result={session.result}
        onPick={(label) => session.setSelected(label)}
      />

      {session.result ? (
        <ExplanationCard
          result={session.result}
          footer={
            <ReportSection
              current={session.current}
              open={report.open}
              setOpen={report.setOpen}
              reason={report.reason}
              setReason={report.setReason}
              comment={report.comment}
              setComment={report.setComment}
              submitting={report.submitting}
              sent={report.sent}
              onSubmit={report.submit}
            />
          }
        />
      ) : null}

      {session.result && !session.result.correct && tutor.messages.length > 0 ? (
        <TutorPanel
          messages={tutor.messages}
          input={tutor.input}
          setInput={tutor.setInput}
          sending={tutor.sending}
          error={tutor.error}
          userTurnCount={tutor.userTurnCount}
          capped={tutor.capped}
          onSend={tutor.send}
          onAdvance={handleAdvance}
        />
      ) : null}

      {session.isAnonymous && session.result && !session.result.correct ? (
        <View style={styles.nudgeCard}>
          <Text style={styles.nudgeTitle}>Want to know why?</Text>
          <Text style={styles.nudgeBody}>
            Create a free account and the tutor walks you through every miss —
            plus 15 more free questions and your readiness diagnostic.
          </Text>
          <Link href="/signup" asChild>
            <Button
              label="Create a free account"
              accessibilityLabel="Create a free account to unlock the tutor"
              fullWidth
            />
          </Link>
        </View>
      ) : null}

      {session.error ? (
        <Text style={styles.error}>
          Something went wrong. Please try again.
        </Text>
      ) : null}
    </>
  );

  const footer = showPinnedFooter ? (
    <PinnedFooter
      bottomInset={insets.bottom}
      canSubmit={session.selected !== null}
      submitting={session.submitting}
      answered={session.result !== null}
      isLast={isLast}
      onSubmit={handleSubmit}
      onNext={handleAdvance}
    />
  ) : null;

  // Rule A (design_handoff_2): ≥900 splits into a pinned left "read this"
  // pane (topic + stem) and a scrolling right "act on this" pane.
  if (isWide) {
    return (
      <Screen>
        <View style={styles.splitRow}>
          <View style={styles.leftPane}>{stem}</View>
          <ScrollView
            ref={rightPaneScrollRef}
            style={styles.rightPane}
            contentContainerStyle={styles.rightContent}
            keyboardShouldPersistTaps="handled"
          >
            {progress}
            {answerStack}
          </ScrollView>
        </View>
        {footer}
      </Screen>
    );
  }

  // Focused column (< 900) — one thing at a time, centered.
  return (
    <Screen>
      <ScrollView
        ref={focusedScrollRef}
        contentContainerStyle={styles.scrollContainer}
        keyboardShouldPersistTaps="handled"
      >
        {progress}
        {stem}
        {answerStack}
      </ScrollView>
      {footer}
    </Screen>
  );
}

function makeStyles(colors: Colors) {
  return StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: colors.background,
      alignItems: "center",
      justifyContent: "center",
      padding: space[6],
    },
    scrollContainer: {
      padding: space[6],
      gap: space[4],
      maxWidth: layout.focusedMaxWidth,
      alignSelf: "center",
      width: "100%",
    },
    // Rule A split (≥900): left "read" pane + right "act" pane.
    splitRow: { flex: 1, flexDirection: "row" },
    leftPane: {
      flexBasis: "44%",
      flexGrow: 0,
      backgroundColor: colors.surface,
      borderRightWidth: 1,
      borderRightColor: colors.borderSoft,
      paddingHorizontal: space[10],
      paddingVertical: space[10],
      justifyContent: "center",
    },
    rightPane: { flex: 1 },
    rightContent: {
      padding: space[8],
      paddingBottom: space[10],
      gap: space[4],
      maxWidth: 620,
      width: "100%",
    },
    errorTitle: {
      ...textStyle.bodyLarge,
      fontFamily: fontFamily.bold,
      color: colors.textPrimary,
      textAlign: "center",
      marginBottom: space[2],
    },
    error: {
      ...textStyle.bodySmall,
      color: colors.error,
      textAlign: "center",
    },
    nudgeCard: {
      backgroundColor: colors.surface,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: radius.lg,
      padding: space[5],
      gap: space[3],
    },
    nudgeTitle: {
      ...textStyle.bodySmall,
      fontFamily: fontFamily.bold,
      color: colors.textPrimary,
    },
    nudgeBody: {
      ...textStyle.bodySmall,
      color: colors.textSecondary,
    },
    button: {
      marginTop: space[3],
    },
  });
}
